import { useState, useEffect } from "react";

function calculateArea(polygon) {
  let area = 0;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }
  return Math.abs(area / 2);
}

function getBoundingBox(polygon) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
}

// Resize a room polygon by adjusting the bounding box to new width/height
// Keeps the top-left corner anchored, scales all vertices proportionally
function resizePolygon(polygon, newWidth, newHeight) {
  const bbox = getBoundingBox(polygon);
  if (bbox.width === 0 || bbox.height === 0) return polygon;

  const scaleX = newWidth / bbox.width;
  const scaleY = newHeight / bbox.height;

  return polygon.map((p) => ({
    x: Math.round((bbox.minX + (p.x - bbox.minX) * scaleX) * 10) / 10,
    y: Math.round((bbox.minY + (p.y - bbox.minY) * scaleY) * 10) / 10,
  }));
}

function RoomEditor({ room, onUpdate, onDelete, onDeselect }) {
  const [name, setName] = useState(room.name);
  const bbox = getBoundingBox(room.polygon);
  const [width, setWidth] = useState(bbox.width.toFixed(1));
  const [height, setHeight] = useState(bbox.height.toFixed(1));
  const area = calculateArea(room.polygon);

  // Sync local state when selected room changes
  useEffect(() => {
    setName(room.name);
    const b = getBoundingBox(room.polygon);
    setWidth(b.width.toFixed(1));
    setHeight(b.height.toFixed(1));
  }, [room.id, room.polygon]);

  const handleNameBlur = () => {
    if (name !== room.name) {
      onUpdate({ name });
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const handleColorChange = (e) => {
    onUpdate({ color: e.target.value });
  };

  const handleWidthBlur = () => {
    const newW = parseFloat(width);
    if (isNaN(newW) || newW <= 0) {
      setWidth(bbox.width.toFixed(1));
      return;
    }
    const newPolygon = resizePolygon(room.polygon, newW, bbox.height);
    onUpdate({ polygon: newPolygon });
  };

  const handleHeightBlur = () => {
    const newH = parseFloat(height);
    if (isNaN(newH) || newH <= 0) {
      setHeight(bbox.height.toFixed(1));
      return;
    }
    const newPolygon = resizePolygon(room.polygon, bbox.width, newH);
    onUpdate({ polygon: newPolygon });
  };

  const handleDimKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  return (
    <div className="room-editor">
      <div className="room-editor-header">
        <h3>Room Editor</h3>
        <button className="btn-icon" onClick={onDeselect} title="Deselect">
          ✕
        </button>
      </div>

      <div className="room-editor-field">
        <label>Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
        />
      </div>

      <div className="room-editor-field">
        <label>Color</label>
        <div className="color-row">
          <input
            type="color"
            value={room.color}
            onChange={handleColorChange}
          />
          <span className="color-value">{room.color}</span>
        </div>
      </div>

      <div className="room-editor-dimensions">
        <div className="dim-field">
          <label>Width (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.5"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            onBlur={handleWidthBlur}
            onKeyDown={handleDimKeyDown}
          />
        </div>
        <div className="dim-field">
          <label>Depth (m)</label>
          <input
            type="number"
            step="0.1"
            min="0.5"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onBlur={handleHeightBlur}
            onKeyDown={handleDimKeyDown}
          />
        </div>
        <div className="dim-field dim-readonly">
          <label>Area</label>
          <span className="dim-value">{area.toFixed(1)} m²</span>
        </div>
      </div>

      <div className="room-editor-vertices">
        <h4>Vertices ({room.polygon.length})</h4>
        <div className="vertex-list">
          {room.polygon.map((p, i) => (
            <div key={i} className="vertex-row">
              <span className="vertex-index">{i + 1}</span>
              <span className="vertex-coord">
                ({p.x.toFixed(1)}, {p.y.toFixed(1)})
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="room-editor-actions">
        <button className="btn-secondary btn-danger" onClick={onDelete}>
          Delete Room
        </button>
      </div>
    </div>
  );
}

export default RoomEditor;
